// Builder working-state model (slice 4a-3).
//
// The wizard edits a local copy of the program (the "working draft") for
// snappy typing, and a debounced autosave PATCHes the changed fields to the
// API. The server is the source of truth for `status` / `activated_at` etc.;
// the working draft only carries the owner-editable fields.

import {
  buildDefaultProgramDraft,
  type BackLink,
  type CompletionBehaviorType,
  type EventTicketConfig,
  type ExpirationMode,
  type ExpirationRelativeUnit,
  type MembershipConfig,
  type Milestone,
  type OnboardingFields,
  type PassDesign,
  type Program,
  type ReferralConfig,
} from '@naya/shared'

/**
 * The owner-editable subset of a program — what the wizard mutates.
 *
 * SLICE-MEMBERSHIP-PASS — one working draft serves BOTH pass types. For a
 * membership program the loyalty structural fields (`total_stamps`,
 * `completion_behavior`, `milestones`) are null on the server; we coalesce
 * them to safe loyalty defaults here so the shape stays non-null, but the
 * membership autosave NEVER diffs/patches them (the API 400s on loyalty
 * structural fields for a membership). `type_config` is non-null exactly when
 * `pass_type === 'membership'`.
 */
export interface WorkingDraft {
  name: string
  total_stamps: number
  completion_behavior: CompletionBehaviorType
  milestones: Milestone[]
  onboarding_fields: OnboardingFields
  pass_design: PassDesign
  back_links: BackLink[]
  owner_terms: string
  expiration_mode: ExpirationMode
  expiration_relative_unit: ExpirationRelativeUnit | null
  expiration_relative_amount: number | null
  expiration_fixed_date: string | null
  // SLICE-REFERRAL — referral config carried so the wizard PREVIEW reflects a
  // referral-enabled program. The wizard doesn't edit it (that's the owner
  // Referrals page); loaded-but-unchanged → draftDiff never patches it.
  referral_config: ReferralConfig | null
  // SLICE-MEMBERSHIP-PASS — pass-type discriminant + membership config. For a
  // loyalty program `pass_type === 'loyalty'` and `type_config === null`; for a
  // membership program `pass_type === 'membership'` and `type_config` carries
  // the tier/benefits/label/self-enroll config the membership step edits.
  pass_type: 'loyalty' | 'membership' | 'event_ticket'
  type_config: MembershipConfig | EventTicketConfig | null
}

/** A draft (or program) is a membership when its pass_type says so. */
export function isMembershipDraft(draft: {
  pass_type: WorkingDraft['pass_type']
}): boolean {
  return draft.pass_type === 'membership'
}

/** SLICE-EVENT-TICKET — an event-ticket draft. */
export function isEventTicketDraft(draft: {
  pass_type: WorkingDraft['pass_type']
}): boolean {
  return draft.pass_type === 'event_ticket'
}

/** Any non-loyalty (typed) draft — carries type_config, no stamps/milestones. */
export function isTypedDraft(draft: {
  pass_type: WorkingDraft['pass_type']
}): boolean {
  return draft.pass_type !== 'loyalty'
}

/** Project a server Program row down to the editable working draft. */
export function programToDraft(p: Program): WorkingDraft {
  // Loyalty defaults to fall back on for a membership row, whose structural
  // fields are null on the server. Membership never patches these — they exist
  // only to keep the draft shape non-null for the shared loyalty code paths.
  const loyaltyFallback = buildDefaultProgramDraft()
  const passType: WorkingDraft['pass_type'] =
    p.pass_type === 'membership'
      ? 'membership'
      : p.pass_type === 'event_ticket'
        ? 'event_ticket'
        : 'loyalty'
  return {
    name: p.name,
    total_stamps: p.total_stamps ?? loyaltyFallback.total_stamps,
    completion_behavior:
      p.completion_behavior ?? loyaltyFallback.completion_behavior,
    milestones: p.milestones ?? loyaltyFallback.milestones,
    onboarding_fields: p.onboarding_fields,
    pass_design: p.pass_design,
    back_links: p.back_links,
    owner_terms: p.owner_terms ?? '',
    expiration_mode: p.expiration_mode,
    expiration_relative_unit: p.expiration_relative_unit,
    expiration_relative_amount: p.expiration_relative_amount,
    expiration_fixed_date: p.expiration_fixed_date,
    referral_config: p.referral_config,
    pass_type: passType,
    // `p.type_config` is the shared TypeConfig union. The cast is safe — the
    // row's pass_type guarantees which shape it carries.
    type_config:
      passType === 'membership'
        ? (p.type_config as MembershipConfig | null)
        : passType === 'event_ticket'
          ? (p.type_config as EventTicketConfig | null)
          : null,
  }
}

/**
 * Shallow diff of two working drafts — returns only the keys that changed, in
 * the ProgramDraftPatch shape. Used by the autosave so a PATCH carries just
 * the touched fields (and structural-lock errors only fire on real changes).
 * Object/array fields are compared by JSON value.
 */
export function draftDiff(
  prev: WorkingDraft,
  next: WorkingDraft,
): Partial<WorkingDraft> {
  const patch: Partial<WorkingDraft> = {}
  for (const key of Object.keys(next) as (keyof WorkingDraft)[]) {
    const a = prev[key]
    const b = next[key]
    const changed =
      typeof b === 'object'
        ? JSON.stringify(a) !== JSON.stringify(b)
        : a !== b
    if (changed) {
      // The cast is safe — key/value are paired by construction.
      ;(patch as Record<string, unknown>)[key] = b
    }
  }
  return patch
}

/** The single `type:'final'` milestone — the grand prize. */
export function grandPrize(milestones: Milestone[]): Milestone | undefined {
  return milestones.find((m) => m.type === 'final')
}

/**
 * Keep the milestone list internally consistent after a stamps-required
 * change: the grand prize is always pinned to `at_stamp === total_stamps`, and
 * any intermediate milestone now past the end is clamped. The builder calls
 * this whenever `total_stamps` changes so the owner can't leave the program in
 * a state the activation check would reject.
 */
export function reconcileMilestones(
  milestones: Milestone[],
  totalStamps: number,
): Milestone[] {
  return milestones.map((m) => {
    if (m.type === 'final') return { ...m, at_stamp: totalStamps }
    if (m.at_stamp >= totalStamps) {
      return { ...m, at_stamp: Math.max(1, totalStamps - 1) }
    }
    return m
  })
}

/**
 * Drop any pass-front detail field whose onboarding field is no longer
 * enabled. The builder calls this when `onboarding_fields` changes so a field
 * the owner just turned OFF in step 1 can't linger in `pass_design.front_fields`
 * (the preview already guards on render, but the stored value should stay clean
 * too — otherwise re-enabling the field silently resurrects it on the pass).
 */
export function reconcileFrontFields(draft: WorkingDraft): WorkingDraft {
  const pruned = draft.pass_design.front_fields.filter(
    (key) => draft.onboarding_fields[key]?.enabled,
  )
  if (pruned.length === draft.pass_design.front_fields.length) return draft
  return {
    ...draft,
    pass_design: { ...draft.pass_design, front_fields: pruned },
  }
}

/**
 * Pass expiration is only valid on a 'stay_completed' program — a 'reset'
 * card recycles forever. When the owner flips completion_behavior back to
 * 'reset', clear any expiry config so the draft can't carry an expiration
 * the server would reject. The builder calls this on every update (cheap;
 * a no-op when the config is already clean).
 */
export function reconcileExpiration(draft: WorkingDraft): WorkingDraft {
  if (draft.completion_behavior === 'stay_completed') return draft
  if (draft.expiration_mode === 'none') return draft
  return {
    ...draft,
    expiration_mode: 'none',
    expiration_relative_unit: null,
    expiration_relative_amount: null,
    expiration_fixed_date: null,
  }
}

// Wizard steps. The landing page routes an active program straight to the
// cosmetic steps (3 and 4 only); a draft walks all five.
export const WIZARD_STEPS = [
  { key: 'onboarding', label: 'Sign-up info' },
  { key: 'program', label: 'Program' },
  { key: 'front', label: 'Pass front' },
  { key: 'back', label: 'Pass back' },
  { key: 'review', label: 'Review' },
] as const

// SLICE-MEMBERSHIP-PASS — membership wizard steps. Simpler than loyalty: no
// stamps/milestones step. The 'membership' step replaces loyalty's 'program'
// step (tier / member number / benefits / self-enroll); the 'expiry' step is
// membership-only (loyalty folds expiry into its 'program' step, gated to
// stay_completed). 'onboarding', 'front', 'back', 'review' are shared.
export const MEMBERSHIP_WIZARD_STEPS = [
  { key: 'membership', label: 'Membership' },
  { key: 'onboarding', label: 'Sign-up info' },
  { key: 'front', label: 'Pass front' },
  { key: 'back', label: 'Pass back' },
  { key: 'expiry', label: 'Expiry' },
  { key: 'review', label: 'Review' },
] as const

// SLICE-EVENT-TICKET — event wizard steps. The 'event' step combines the event
// details (name/date/venue/capacity) + the tiers editor; the rest are shared.
// No stamps/milestones/expiry (a ticket's validity is its event date).
export const EVENT_TICKET_WIZARD_STEPS = [
  { key: 'event', label: 'Event' },
  { key: 'onboarding', label: 'Sign-up info' },
  { key: 'front', label: 'Pass front' },
  { key: 'back', label: 'Pass back' },
  { key: 'review', label: 'Review' },
] as const

// The 'front' + 'back' steps are the cosmetic ones — an active program enters
// the wizard restricted to these (all types share the same cosmetic steps).
export type WizardStepKey =
  | (typeof WIZARD_STEPS)[number]['key']
  | (typeof MEMBERSHIP_WIZARD_STEPS)[number]['key']
  | (typeof EVENT_TICKET_WIZARD_STEPS)[number]['key']
